import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    ButtonProps,
    DialogProps,
    FormControl,
    Grid,
    IconButton,
    IconButtonProps,
    MenuItem,
    Button as MuiButton,
    Dialog as MuiDialog,
    Select,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import dayjs, { Dayjs } from "dayjs";
import React, { useContext } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { mutationCreateMap, mutationUpdateMap } from "src/api/map";
import { Enum_Map_Types } from "src/api/map/enum";
import { Type_LineString, Type_Map } from "src/api/map/types";
import { MapContext } from "src/contexts/map";
import { SettingContext } from "src/contexts/settings";
import MuiDateTimePicker from "src/genericMuiInputs/DateTimePicker";
import { Enum_Geometry_Types } from "src/types";
import { createMapSchema } from "src/validators/CreateMap";

type Type_Props_Create_Map = {
    close: () => void;
};

export type Type_Create_Map_Data = {
    label: string;
    type: string;
    url: string;
    bound: Type_LineString;
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    min_zoom: number;
    max_zoom: number;
    end_date: Dayjs | null;
    start_date: Dayjs;
    user_id: number;
};

const Icon = styled(IconButton)<IconButtonProps>(() => ({
    position: "absolute",
    top: 10,
    right: 10,
}));

const Button = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const Dialog = styled(MuiDialog)<DialogProps>(({ theme }) => ({
    "& .MuiDialog-paper": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "70%",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "100%",
        },
    },
}));

const CreateMap: React.FC<Type_Props_Create_Map> = ({ close }) => {
    const { t } = useTranslation();

    const { map } = useContext(SettingContext);
    const { connectedUser } = useContext(MapContext);

    const methods = useForm({
        resolver: yupResolver(createMapSchema(t)),
        defaultValues: {
            label: map?.label || "",
            type: map?.type || Enum_Map_Types.STANDARD_OSM,
            url: map?.url || "",
            minX: map?.bound[0][0] || 0,
            minY: map?.bound[0][1] || 0,
            maxX: map?.bound[1][0] || 0,
            maxY: map?.bound[1][1] || 0,
            min_zoom: map?.minZoom || 0,
            max_zoom: map?.maxZoom || 0,
            start_date: map?.startDate || dayjs(),
            end_date: map?.endDate || null,
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = methods;

    const { mutateAsync: mutateCreateMap } = mutationCreateMap();
    const { mutateAsync: mutateUpdateMap } = mutationUpdateMap();

    const mapType = watch("type");
    const queryClient = useQueryClient();

    const onSubmit = async (data: Partial<Type_Map>) => {
        const mapObject: Partial<Type_Map> = {
            url: data.url,
            min_zoom: data.min_zoom,
            max_zoom: data.max_zoom,
            label: data.label,
            start_date: data.start_date,
            end_date: data.end_date ?? null,
            type: data.type,
            bound: {
                type: Enum_Geometry_Types.LINESTRING,
                coordinates: [
                    [data.minX!, data.minY!],
                    [data.maxX!, data.maxY!],
                ],
            },
            user_id: connectedUser?.id,
        };

        if (!mapObject.end_date) delete mapObject.end_date;

        if (!map) await mutateCreateMap(mapObject);
        else await mutateUpdateMap({ mapId: map.id, body: mapObject });

        close();
        queryClient.invalidateQueries(["maps"]);
    };

    return (
        <Dialog open onClose={close} fullWidth maxWidth="md">
            <Icon disableRipple onClick={close} size="small">
                <FontAwesomeIcon icon={faTimes} />
            </Icon>

            <Box width="90%">
                <Typography mb={3} variant="h5">
                    {map ? t("maps.update_map") : t("maps.create_map")}
                </Typography>

                <FormProvider {...methods}>
                    <Grid
                        container
                        component="form"
                        width="100%"
                        onSubmit={handleSubmit(
                            (data) =>
                                void onSubmit(data as unknown as Type_Map),
                        )}
                        spacing={2}
                    >
                        <Grid item xs={9}>
                            <Typography color="text.secondary">
                                {t("maps.label")}
                            </Typography>
                            <Controller
                                name="label"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.label}
                                        helperText={errors.label?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={5}>
                            <Typography color="text.secondary">
                                {t("maps.type")}
                            </Typography>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={!!errors.type}
                                    >
                                        <Select {...field}>
                                            {Object.values(Enum_Map_Types).map(
                                                (value) => (
                                                    <MenuItem
                                                        key={value}
                                                        value={value}
                                                    >
                                                        {t(`maps.${value}`)}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={7}>
                            <Typography color="text.secondary">
                                {t("maps.url")}
                            </Typography>
                            <Controller
                                name="url"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...(mapType ===
                                        Enum_Map_Types.STANDARD_OSM
                                            ? field
                                            : {})}
                                        fullWidth
                                        size="small"
                                        type={
                                            mapType ===
                                            Enum_Map_Types.STANDARD_OSM
                                                ? "text"
                                                : "file"
                                        }
                                        error={!!errors.url}
                                        helperText={errors.url?.message}
                                        onChange={async (event) => {
                                            if (
                                                mapType !==
                                                Enum_Map_Types.STANDARD_OSM
                                            ) {
                                                const file = (
                                                    event.target as HTMLInputElement
                                                ).files?.[0];
                                                if (file) {
                                                    const reader =
                                                        new FileReader();
                                                    reader.onloadend = () => {
                                                        field.onChange(
                                                            reader.result,
                                                        );
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            } else {
                                                field.onChange(
                                                    event.target.value,
                                                );
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography color="text.secondary">
                                {t("maps.min_x")}
                            </Typography>
                            <Controller
                                name="minX"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.minX}
                                        helperText={errors.minX?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography color="text.secondary">
                                {t("maps.min_y")}
                            </Typography>
                            <Controller
                                name="minY"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.minY}
                                        helperText={errors.minY?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography color="text.secondary">
                                {t("maps.max_x")}
                            </Typography>
                            <Controller
                                name="maxX"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.maxX}
                                        helperText={errors.maxX?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography color="text.secondary">
                                {t("maps.max_y")}
                            </Typography>
                            <Controller
                                name="maxY"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.maxY}
                                        helperText={errors.maxY?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("maps.min_zoom")}
                            </Typography>
                            <Controller
                                name="min_zoom"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.min_zoom}
                                        helperText={errors.min_zoom?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("maps.max_zoom")}
                            </Typography>
                            <Controller
                                name="max_zoom"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        error={!!errors.max_zoom}
                                        helperText={errors.max_zoom?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("maps.start_date")}
                            </Typography>
                            <MuiDateTimePicker
                                rules={{
                                    required: t(
                                        "addElement.validator.required",
                                    ),
                                }}
                                name="start_date"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("maps.end_date")}
                            </Typography>
                            <MuiDateTimePicker
                                rules={{
                                    required: t(
                                        "addElement.validator.required",
                                    ),
                                }}
                                name="end_date"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="flex-end"
                        >
                            <Button
                                type="submit"
                                size="large"
                                variant="contained"
                                color="primary"
                            >
                                {t("validate")}
                            </Button>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>
        </Dialog>
    );
};

export default CreateMap;
